import type {
  TAssessmentDTO,
  TBookingMemberDTO,
  TConciergeSessionDTO,
  TInterviewAttemptsDTO,
  TInterviewDTO,
  TStaffInterviewDTO,
} from './interviews.dto';

import type {
  TAssessment,
  TConciergeSession,
  TInterview,
  TInterviewAttempts,
  TInterviewStatus,
  TStaffInterview,
} from './interviews.types';

import { COMPLETED_END_REASON } from './interviews.fields';

const toInterviewStatus = (dto: TStaffInterviewDTO): TInterviewStatus => {
  if (dto.no_show_at !== null) {
    return 'no_show';
  }

  if (dto.completed_at === null && new Date(dto.starts_at).getTime() > Date.now()) {
    return 'upcoming';
  }

  if (dto.interview_assessments === null) {
    return 'assessment_due';
  }

  return 'completed';
};

const toStaffInterview = (dto: TStaffInterviewDTO): TStaffInterview => ({
  bookingID: dto.id,
  applicantID: dto.member_id,
  applicantName: dto.applicant ? `${dto.applicant.first_name} ${dto.applicant.last_name}` : '',
  applicantRole: dto.applicant?.role ?? null,
  kind: dto.kind,
  startsAt: dto.starts_at,
  interviewerID: dto.interviewer_id,
  status: toInterviewStatus(dto),
});

const toAssessment = (dto: TAssessmentDTO): TAssessment => ({
  id: dto.id,
  bookingID: dto.booking_id,
  firstPass: dto.first_pass,
  appearance: dto.appearance,
  redFlags: dto.red_flags,
  communication: dto.communication,
  personality: dto.personality,
  overall: dto.overall,
  assessedAt: dto.assessed_at,
  assessedBy: dto.assessed_by,
});

const toInterview = (dto: TInterviewDTO): TInterview => ({
  bookingID: dto.id,
  applicantID: dto.member_id,
  startsAt: dto.starts_at,
  canceledAt: dto.canceled_at,
  noShowAt: dto.no_show_at,
  completedAt: dto.completed_at,
  allowedChannels: dto.allowed_channels,
  meetingURL: dto.meeting_url,
  createdAt: dto.created_at,
  assessment: dto.interview_assessments === null ? null : toAssessment(dto.interview_assessments),
});

const toConciergeSession = (dto: TConciergeSessionDTO): TConciergeSession => ({
  bookingID: dto.id,
  memberID: dto.member_id,
  startsAt: dto.starts_at,
  completedAt: dto.completed_at,
  isAccessOpen: dto.end_reason === COMPLETED_END_REASON,
});

const toMemberID = (dto: TBookingMemberDTO): string => dto.user_id;

const toInterviewAttempts = (dto: TInterviewAttemptsDTO): TInterviewAttempts => ({
  used: dto.used,
  max: dto.max,
});

export {
  toConciergeSession,
  toInterviewAttempts,
  toMemberID,
  toAssessment,
  toInterview,
  toStaffInterview,
};
